// 004 (FR-014, FR-015; P-ADD-1, BR-14; data-model §6.2): class and section chosen only from the
// school's list, in the server's order. No free text. A class with one section preselects it;
// choosing another class clears the section.
import { StyleSheet, View } from "react-native";

import type { Api } from "@/api/types";
import { makeStyles } from "@/theme/makeStyles";
import { PressableTarget } from "@/ui/PressableTarget";
import { Text } from "@/ui/Text";

import { PROFILE_COPY } from "./copy";

export type ClassSection = {
  classLevel: string | null;
  section: string | null;
};

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    group: { gap: t.space.sm },
    row: { flexDirection: "row", flexWrap: "wrap", gap: t.space.sm },
    chip: {
      paddingHorizontal: t.space.md,
      borderRadius: t.radius.pill,
      borderWidth: t.borderWidth.input,
      borderColor: t.color.border,
      backgroundColor: t.color.surface,
    },
    chipOn: {
      borderColor: t.color.primary,
      backgroundColor: t.color.primarySoft,
    },
    error: { color: t.color.danger },
  }),
);

function Chip({
  label,
  name,
  on,
  onPress,
  disabled,
}: {
  label: string;
  name?: string;
  on: boolean;
  onPress: () => void;
  disabled?: boolean;
}) {
  const styles = useStyles();
  return (
    <PressableTarget
      accessibilityRole="radio"
      accessibilityLabel={name ?? label}
      accessibilityState={{ checked: on, disabled }}
      aria-checked={on}
      disabled={disabled}
      onPress={onPress}
      style={on ? [styles.chip, styles.chipOn] : styles.chip}
    >
      <Text weight={on ? "semiBold" : "regular"}>{label}</Text>
    </PressableTarget>
  );
}

export function ClassSectionPickers({
  classes,
  schoolName,
  value,
  onChange,
  errors = {},
  disabled = false,
}: {
  classes: readonly Api["SchoolClass"][];
  schoolName: string;
  value: ClassSection;
  onChange: (next: ClassSection) => void;
  errors?: { classLevel?: string; section?: string };
  disabled?: boolean;
}) {
  const styles = useStyles();
  if (classes.length === 0) {
    return (
      <Text role="alert" testID="no-classes">
        {PROFILE_COPY.noClasses(schoolName)}
      </Text>
    );
  }
  const current =
    classes.find((c) => c.classLevel === value.classLevel) ?? null;
  return (
    <View style={styles.group}>
      <Text weight="semiBold" nativeID="class-label">
        {PROFILE_COPY.classLabel}
      </Text>
      <View style={styles.row} role="radiogroup" aria-labelledby="class-label">
        {classes.map((c) => (
          <Chip
            key={c.classLevel}
            label={c.name}
            on={c.classLevel === value.classLevel}
            disabled={disabled}
            onPress={() =>
              onChange({
                classLevel: c.classLevel,
                section:
                  c.sections.length === 1
                    ? (c.sections[0] ?? null)
                    : c.classLevel === value.classLevel
                      ? value.section
                      : null,
              })
            }
          />
        ))}
      </View>
      {errors.classLevel ? (
        <Text style={styles.error}>{errors.classLevel}</Text>
      ) : null}
      <Text weight="semiBold" nativeID="section-label">
        {PROFILE_COPY.sectionLabel}
      </Text>
      {current === null ? (
        <Text tone="muted">{PROFILE_COPY.chooseClassFirst}</Text>
      ) : (
        <View
          style={styles.row}
          role="radiogroup"
          aria-labelledby="section-label"
        >
          {current.sections.map((s) => (
            <Chip
              key={s}
              label={s}
              name={`Section ${s}`}
              on={s === value.section}
              disabled={disabled}
              onPress={() =>
                onChange({ classLevel: current.classLevel, section: s })
              }
            />
          ))}
        </View>
      )}
      {errors.section ? (
        <Text style={styles.error}>{errors.section}</Text>
      ) : null}
    </View>
  );
}
